import moment from "moment";
import { createSelector } from "@reduxjs/toolkit";
import { type ColumnDataPoint } from "../../../../../components/charts/types/ColumnChart";
import { type DataPoint } from "../../../../../components/charts/types/HorizontalBarChart";
import { selectTeamsDeviceUsage } from "../graphSlice";
import { reportingColors } from "features/Library/Common/utils/constants";

// For top column chart
export const selectActiveUsersSummations = createSelector(selectTeamsDeviceUsage, (state) => {
  return state.value.map((s) => ({
    date: moment.utc(s.ReportDate).toDate(),
    totalCount: s.ActiveUserCount,
  }));
});

/**
 * For lower line chart
 */
export const selectUsageLineData = createSelector(selectTeamsDeviceUsage, ({ value }) => {
  const totalCount = Math.max(value.length, 1); // Prevent divide by 0 error
  let returnVal = {
    Android: Array<number>(totalCount),
    ChromeOS: Array<number>(totalCount),
    dates: Array<Date>(totalCount),
    Linux: Array<number>(totalCount),
    Mac: Array<number>(totalCount),
    Web: Array<number>(totalCount),
    Windows: Array<number>(totalCount),
    WindowsPhone: Array<number>(totalCount),
    iOS: Array<number>(totalCount),
  };
  value.forEach((item, index) => {
    const totalSum = item.ActiveUserCount;
    returnVal.Android[index] = Math.round((item.Android / totalSum) * 100) || 0;
    returnVal.ChromeOS[index] = Math.round((item.ChromeOS / totalSum) * 100) || 0;
    returnVal.Linux[index] = Math.round((item.Linux / totalSum) * 100) || 0;
    returnVal.Mac[index] = Math.round((item.Mac / totalSum) * 100) || 0;
    returnVal.Web[index] = Math.round((item.Web / totalSum) * 100) || 0;
    returnVal.Windows[index] = Math.round((item.Windows / totalSum) * 100) || 0;
    returnVal.WindowsPhone[index] = Math.round((item.WindowsPhone / totalSum) * 100) || 0;
    returnVal.iOS[index] = Math.round((item.iOS / totalSum) * 100) || 0;

    returnVal.dates[index] = moment.utc(item.ReportDate).toDate();
  });

  return returnVal;
});

type PossibleDevices = {
  Android: number;
  ChromeOS: number;
  Linux: number;
  Mac: number;
  Web: number;
  Windows: number;
  WindowsPhone: number;
  iOS: number;
};

export const selectUsageByDevicePercentages = createSelector(selectTeamsDeviceUsage, (state) => {
  const categorySums: PossibleDevices = state.value.reduce(
    (prev, curr) => ({
      Android: prev.Android + curr.Android,
      ChromeOS: prev.ChromeOS + curr.ChromeOS,
      Linux: prev.Linux + curr.Linux,
      Mac: prev.Mac + curr.Mac,
      Web: prev.Web + curr.Web,
      Windows: prev.Windows + curr.Windows,
      WindowsPhone: prev.WindowsPhone + curr.WindowsPhone,
      iOS: prev.iOS + curr.iOS,
    }),
    {
      Android: 0,
      ChromeOS: 0,
      Linux: 0,
      Mac: 0,
      Web: 0,
      Windows: 0,
      WindowsPhone: 0,
      iOS: 0,
    },
  );

  const totalSum = Object.values(categorySums).reduce((a, v) => a + v, 0);

  const categoryPercentages: PossibleDevices = {
    Android: 0,
    ChromeOS: 0,
    Linux: 0,
    Mac: 0,
    Web: 0,
    Windows: 0,
    WindowsPhone: 0,
    iOS: 0,
  };

  (Object.keys(categorySums) as Array<keyof typeof categorySums>).forEach((key) => {
    categoryPercentages[key] = Math.round((categorySums[key] / totalSum) * 100);
  });

  return {
    categorySums,
    categoryPercentages,
  };
});

// For column chart in device usage
export const selectFormattedColumnData = createSelector(selectActiveUsersSummations, (arg): ColumnDataPoint[] => {
  return arg.map((a, i) => ({
    id: i.toString(),
    category: a.date.toISOString(),
    value: a.totalCount,
    fillColor: reportingColors.blue,
  }));
});

// For bar chart
export const selectFormattedBarData = createSelector(selectUsageByDevicePercentages, (devices): DataPoint[] => {
  const entries = Object.entries(devices.categoryPercentages).map<DataPoint>(([category, value]) => ({
    id: category,
    category,
    value,
    fillColor: reportingColors.blue,
  }));
  entries.sort((a, b) => b.value - a.value);
  return entries;
});
